import type { ParamSchema } from '../../content/registry/objectRegistry';

/**
 * Built-in parameter schemas for the engine's neutral assets (lights, triggers,
 * markers, decals). Used alongside any schema declared on the registry entry
 * (the registry entry wins on conflicts). Games register schemas for their own
 * props via `registerParamSchemas`, mirroring `registerObjects`.
 */
export const BUILTIN_PARAM_SCHEMAS: Record<string, Record<string, ParamSchema>> = {
  light_point_warm: {
    intensity: { type: 'number', min: 0, max: 8, step: 0.05 },
    range: { type: 'number', min: 0.5, max: 20, step: 0.1 },
    color: { type: 'color' },
  },
  light_point_cool: {
    intensity: { type: 'number', min: 0, max: 8, step: 0.05 },
    range: { type: 'number', min: 0.5, max: 20, step: 0.1 },
    color: { type: 'color' },
  },
  light_spot: {
    intensity: { type: 'number', min: 0, max: 12, step: 0.1 },
    range: { type: 'number', min: 0.5, max: 30, step: 0.1 },
    angle: { type: 'number', min: 0.05, max: Math.PI / 2, step: 0.01 },
    color: { type: 'color' },
  },
  trigger_teleport: {
    targetFloor: { type: 'number', min: -1, max: 1, step: 1, integer: true },
    targetX: { type: 'number', min: -80, max: 80, step: 0.1 },
    targetZ: { type: 'number', min: -80, max: 80, step: 0.1 },
  },
  trigger_event: {
    eventName: { type: 'string', maxLength: 64 },
    oneShot: { type: 'boolean' },
  },
  trigger_jumpscare: {
    jumpscareId: { type: 'string', maxLength: 64 },
    oneShot: { type: 'boolean' },
  },
  marker_checkpoint: {
    checkpointId: { type: 'string', maxLength: 64 },
  },
  marker_event: {
    eventName: { type: 'string', maxLength: 64 },
  },
  decal_wet_floor: {
    opacity: { type: 'number', min: 0, max: 1, step: 0.05 },
  },
  decal_stain: {
    opacity: { type: 'number', min: 0, max: 1, step: 0.05 },
  },
  decal_crack: {
    opacity: { type: 'number', min: 0, max: 1, step: 0.05 },
  },
};

// ── Mutable registry ──────────────────────────────────────────────────────────
// Seeded with the engine built-ins; games add prop schemas via
// registerParamSchemas. Per-asset schemas merge (later registrations win on a
// per-key basis).
const REGISTRY = new Map<string, Record<string, ParamSchema>>(
  Object.entries(BUILTIN_PARAM_SCHEMAS),
);

/** Register (or merge) parameter schemas keyed by assetId. Idempotent per key. */
export function registerParamSchemas(
  schemas: Record<string, Record<string, ParamSchema>>,
): void {
  for (const [assetId, schema] of Object.entries(schemas)) {
    REGISTRY.set(assetId, { ...REGISTRY.get(assetId), ...schema });
  }
}

/** The default schemas registered for an asset, or undefined when none. */
export function getDefaultParamSchemas(
  assetId: string,
): Record<string, ParamSchema> | undefined {
  return REGISTRY.get(assetId);
}
